
import {
  CSMLSortBy,
  CSMLImportSource,
} from '@bingads-webui-campaign-react/ads-studio-app-shared-components';
import { stringifySync } from '@bingads-webui/safe-json';
import { Promise } from 'bluebird';
import _ from 'underscore';

import { formatCivilDate, removeNullUndefined } from '../service-utils';
import { transformImageResponse } from './asset-response-transformers/image-response-transformer';

const MAX_TIMEOUT = 5 * 60 * 1000; // set timeout to be 5 minutes
const POLL_INTERVAL = 1000; // set to 1000ms for now
const MediaRepositoryTimeoutError = 'MediaRepositoryTimeoutError';

export const getRepository = ({
  imageBulkEditODataService,
  imageODataService,
  i18n,
  ianaTimeZone,
  timezoneUTCOffset,
}) => {
  const getImages = ({
    searchTerm,
    minWidth,
    minHeight,
    dateAddedStart,
    dateAddedEnd,
    importSource,
    sortBy,
    skip,
    count,
  }) => {
    //
    const args = {
      searchTerm: encodeURIComponent(searchTerm),
      minWidth,
      minHeight,
      dateAddedStart: dateAddedStart && formatCivilDate(i18n, dateAddedStart, ianaTimeZone, timezoneUTCOffset, '00:00:00'),
      dateAddedEnd: dateAddedEnd && formatCivilDate(i18n, dateAddedEnd, ianaTimeZone, timezoneUTCOffset, '23:59:59'),
      skip,
      count,
    };

    if (importSource === CSMLImportSource.Google) {
      args.sourceType = 'GoogleImported';
    } else if (importSource === CSMLImportSource.Facebook) {
      args.sourceType = 'FacebookImported';
    }

    if (sortBy === CSMLSortBy.ImageName) {
      args.sortBy = 'Text';
      args.sortDirection = 'asc';
    } else if (sortBy === CSMLSortBy.MostRecent) {
      args.sortBy = 'ModifiedTime';
      args.sortDirection = 'desc';
    }
    return imageODataService.getImages(args).then((res) => {
      const totalCount = res['@odata.count'] || 0;
      const images = (res.value || []).map(img => transformImageResponse(img, i18n));

      return { images, totalCount };
    });
  };

  const getImage = (imageId, isImageFittingEnabled) =>
    imageODataService.getImageByIdAsync(imageId, isImageFittingEnabled).then(res => transformImageResponse(res, i18n));

  const getImageCroppingTaskStatus = (imageId) => {
    const promise = imageBulkEditODataService.getImageCroppingTaskStatus(imageId).then((res) => {
      const totalCount = res['@odata.count'] || 0;
      const tasks = (res.value || []).map(tsk => ({
        id: `${tsk.AssetId}`,
        name: tsk.ImageText,
        url: tsk.ImageUrl,
        createdTime: tsk.CreatedTime,
        modifiedTime: tsk.ModifiedTime,
        stage: tsk.Stage,
        hasError: tsk.HasError,
        failureMessage: tsk.Checkpoint && tsk.Checkpoint.FailureMessage,
        percentComplete: tsk.Checkpoint && tsk.Checkpoint.PercentComplete,
        imageCroppingTaskStatusId: tsk.ImageCroppingTaskStatusId,
        isFinished: tsk.Stage === 'Completed' || tsk.Stage === 'CompletedWithErrors' || tsk.Stage === 'Failed',
      }));
      return { tasks, totalCount };
    });
    return promise;
  };

  const updateImageData = ({ imageId, imageData, type }) => {
    const promise = imageODataService.updateImageAsync({
      Id: imageId,
      Data: imageData,
      Type: type,
    });
    return promise;
  };

  const renameImage = ({ imageId, name }) => {
    const promise = imageODataService.updateImageAsync({ Id: imageId, Text: name });
    return promise;
  };

  const deleteImage = (imageId) => {
    const promise = imageODataService.deleteImageAsync({ IdString: `${imageId}` });
    return promise;
  };

  const cropImage = ({ imageId, cropSettings, placementSettings }) => {
    const promise = imageBulkEditODataService.cropImage({ imageId, cropSettings, placementSettings })
      .then(res => res.value);
    return promise;
  };

  const uploadImagesByUrl = ({ imageUrls }) => {
    const promise = imageODataService.bulkSaveImagesByUrl(imageUrls);
    return promise;
  };

  const uploadImage = ({
    imageData,
    activityLogger = null,
    updateCurrentProgress = null,
    imageOption = 'Image',
    name = '',
  }) => {
    const isLogo = imageOption === 'Logo';

    const promise = imageODataService.uploadImageAsync(imageData, activityLogger, updateCurrentProgress, isLogo, name);
    return promise;
  };

  const addCreative = ({ creativeData, baseId }) => {
    const serialized = stringifySync(removeNullUndefined(creativeData));
    // todo: save original image and save original image url inside metadata
    const args = [{
      url: creativeData.url,
      metadata: serialized.replace(/,/g, encodeURIComponent(',')),
      baseId,
    }];
    const promise = imageODataService.bulkSaveImagesByUrl(args);
    return promise;
  };

  const saveImageWithBaseId = ({ image, baseId, folderId }) => {
    const { url, metaData } = image;
    const serialized = stringifySync(removeNullUndefined(metaData));
    const args = [{
      url,
      metadata: serialized.replace(/,/g, encodeURIComponent(',')),
      baseId,
      folderId,
    }];
    const promise = imageODataService.bulkSaveImagesByUrl(args);
    return promise;
  };

  const createGenerateRequestWithPolling = ({ effectOptions }) => {
    const timerIds = {};
    const resetTimers = () => {
      if (timerIds.intervalId) {
        clearInterval(timerIds.intervalId);
      }
      if (timerIds.timeoutId) {
        clearTimeout(timerIds.timeoutId);
      }
    };

    return imageBulkEditODataService.generateImageWithSmartEffect({ options: effectOptions }).then((response = {}) => {
      if (!response.wip) {
        return response.Assets;
      }

      return new Promise((resolve, reject) => {
        timerIds.intervalId = setInterval(() => {
          imageBulkEditODataService.generateImageWithSmartEffect({ options: { JobId: response.JobId } })
            .then((newResponse = {}) => {
              if (!newResponse.wip) {
                resetTimers();
                resolve(newResponse.Assets);
              }
            }).catch((error) => {
              resetTimers();
              reject(error);
            });
        }, POLL_INTERVAL);

        timerIds.timeoutId = setTimeout(() => {
          resetTimers();
          reject(new Error(MediaRepositoryTimeoutError));
        }, MAX_TIMEOUT);
      });
    });
  };

  const generateImageWithSmartEffect = ({ imageUrl, smartEffects }) => {
    const effectOptions = {
      Image: {
        BaseImageUrl: imageUrl,
        Filters: _.map(smartEffects, ({ smartEffectName, smartEffectOptions }) => ({
          Name: smartEffectName,
          Param: smartEffectOptions,
        })),
      },
      Output: {
        MediaType: 'image',
        Image: {
          Type: 'jpg',
        },
      },
    };
    return createGenerateRequestWithPolling({ effectOptions });
  };

  const imageFit = ({ imageUrl, placements, skipOutpainting }) => {
    const ret = imageODataService.imageFit({ imageUrl, placements, skipOutpainting });
    return ret;
  };

  return {
    getImages,
    getImage,
    getImageCroppingTaskStatus,
    updateImageData,
    renameImage,
    deleteImage,
    cropImage,
    uploadImagesByUrl,
    uploadImage,
    addCreative,
    generateImageWithSmartEffect,
    saveImageWithBaseId,
    imageFit,
  };
};
